<template>
  <header class="fixed top-0 left-0 right-0 z-50 h-14 sm:h-16 bg-white/90 dark:bg-gray-950/90 backdrop-blur-xl border-b border-gray-200/60 dark:border-gray-800/60 shadow-sm">
    <div class="h-full px-3 sm:px-5 flex items-center justify-between gap-2">

      <!-- ── Logo ── -->
      <NuxtLink to="/" class="flex items-center gap-2 flex-shrink-0 group">
        <div class="w-8 h-8 rounded-xl overflow-hidden ring-2 ring-transparent group-hover:ring-violet-500/40 transition-all duration-200">
          <img src="/assets/images/fave.svg" alt="Logo" class="w-full h-full object-contain" />
        </div>
        <span class="hidden sm:block text-base font-bold text-gray-900 dark:text-white tracking-tight">
          {{ t("nav.dashboard") }}
        </span>
      </NuxtLink>

      <!-- ── Desktop Right Controls ── -->
      <div class="hidden md:flex items-center gap-2.5">

        <!-- Home -->
        <NuxtLink
          to="/"
          class="header-btn"
          :class="$route.path === '/' ? 'header-btn-active' : ''"
          title="Home"
        >
          <UIcon name="i-heroicons-home" class="w-5 h-5" />
        </NuxtLink>

        <!-- ① Main Navigation Dropdown -->
        <div class="relative main-nav-dropdown">
          <button
            @click="toggleMainMenu"
            class="header-btn gap-1.5"
            :class="showMainMenu ? 'header-btn-active' : ''"
            title="Navigation"
          >
            <UIcon name="i-heroicons-rectangle-stack" class="w-5 h-5" />
            <UIcon name="i-heroicons-chevron-down" class="w-3 h-3 transition-transform duration-200" :class="showMainMenu ? 'rotate-180' : ''" />
          </button>

          <Transition name="dropdown">
            <div v-if="showMainMenu" :class="isRTL ? 'left-0' : 'right-0'" class="dropdown-panel w-64">
              <div class="p-2 border-b border-gray-100 dark:border-gray-800">
                <p class="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 px-2 py-1">{{ t('nav.mainNavigation') }}</p>
              </div>
              <div class="p-2 space-y-0.5">
                <NuxtLink
                  v-for="item in mainNavItems"
                  :key="item.to"
                  :to="item.to"
                  v-show="item.show !== false"
                  @click="closeAllMenus"
                  class="dropdown-item"
                  :class="$route.path.startsWith(item.to) ? 'dropdown-item-active' : ''"
                >
                  <span :class="['w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0', item.iconBg]">
                    <UIcon :name="item.icon" class="w-4 h-4" :class="item.iconColor" />
                  </span>
                  <div class="min-w-0">
                    <p class="text-sm font-medium leading-tight">{{ item.label }}</p>
                    <p class="text-xs text-gray-400 dark:text-gray-500 leading-tight">{{ item.desc }}</p>
                  </div>
                </NuxtLink>
              </div>
            </div>
          </Transition>
        </div>

        <!-- Divider -->
        <div class="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1" />

        <!-- ② Notification Bell -->
        <div class="relative notification-dropdown">
          <button @click="toggleNotifications" class="header-btn relative" title="Notifications">
            <UIcon name="i-heroicons-bell" class="w-5 h-5" />
            <span
              v-if="unreadCount > 0"
              class="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[17px] h-[17px] flex items-center justify-center px-1 leading-none"
            >{{ unreadCount > 9 ? '9+' : unreadCount }}</span>
            <span
              v-if="connectionStatus !== 'connected'"
              class="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-white dark:border-gray-950"
              :class="{
                'bg-yellow-400': connectionStatus === 'connecting',
                'bg-orange-400': connectionStatus === 'polling',
                'bg-gray-400': connectionStatus === 'disconnected',
              }"
            />
          </button>

          <Transition name="dropdown">
            <div v-if="showNotifications" :class="isRTL ? 'left-0' : 'right-0'" class="dropdown-panel w-80">
              <!-- Header -->
              <div class="p-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <span class="text-sm font-semibold text-gray-900 dark:text-white">Notifications</span>
                <div class="flex items-center gap-1.5">
                  <span class="w-1.5 h-1.5 rounded-full" :class="{
                    'bg-green-500': connectionStatus === 'connected',
                    'bg-yellow-400': connectionStatus === 'connecting',
                    'bg-orange-400': connectionStatus === 'polling',
                    'bg-gray-400': connectionStatus === 'disconnected',
                  }" />
                  <span class="text-xs text-gray-400 capitalize">{{ connectionStatus }}</span>
                </div>
              </div>
              <!-- List -->
              <div class="max-h-72 overflow-y-auto">
                <div v-if="unreadNotifications.length === 0" class="text-center py-10">
                  <UIcon name="i-heroicons-check-circle" class="w-10 h-10 mx-auto mb-2 text-green-400 dark:text-green-500" />
                  <p class="text-sm font-medium text-gray-600 dark:text-gray-400">{{ t('notifications.allCaughtUp') }}</p>
                  <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ t('notifications.noUnread') }}</p>
                </div>
                <div v-else class="p-2 space-y-0.5">
                  <button
                    v-for="n in unreadNotifications"
                    :key="n.id"
                    @click="handleNotificationClick(n)"
                    class="w-full text-left flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    <span class="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5" />
                    <div class="min-w-0 flex-1">
                      <p class="text-sm text-gray-900 dark:text-white font-medium truncate">{{ n.message }}</p>
                      <p class="text-xs text-gray-400 mt-0.5">{{ formatDate(n.created_at) }}</p>
                    </div>
                  </button>
                </div>
              </div>
              <!-- Footer -->
              <div class="p-2 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <NuxtLink to="/notifications" @click="closeAllMenus" class="text-xs text-violet-600 dark:text-violet-400 hover:underline font-medium">
                  {{ t("notifications.viewAll") }}
                </NuxtLink>
                <span class="text-xs text-gray-400">{{ notifications.length }} total</span>
              </div>
            </div>
          </Transition>
        </div>

        <!-- ③ System Dropdown (Theme + Language) -->
        <div class="relative system-dropdown">
          <button
            @click="toggleSystemMenu"
            class="header-btn gap-1.5"
            :class="showSystemMenu ? 'header-btn-active' : ''"
            title="System"
          >
            <UIcon name="i-heroicons-adjustments-horizontal" class="w-5 h-5" />
            <UIcon name="i-heroicons-chevron-down" class="w-3 h-3 transition-transform duration-200" :class="showSystemMenu ? 'rotate-180' : ''" />
          </button>

          <Transition name="dropdown">
            <div v-if="showSystemMenu" :class="isRTL ? 'left-0' : 'right-0'" class="dropdown-panel w-56">
              <div class="p-2 border-b border-gray-100 dark:border-gray-800">
                <p class="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 px-2 py-1">{{ t('nav.system') }}</p>
              </div>
              <!-- Theme row -->
              <div class="p-3 border-b border-gray-100 dark:border-gray-800">
                <p class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">{{ t('settings.appearance.theme') }}</p>
                <div class="flex items-center gap-1">
                  <button
                    v-for="th in themes"
                    :key="th.value"
                    @click="setTheme(th.value)"
                    :title="th.label"
                    :class="[
                      'flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded-md text-xs font-medium transition-all duration-150',
                      currentTheme === th.value
                        ? 'bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-400 ring-1 ring-violet-500/40'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                    ]"
                  >
                    <UIcon :name="th.icon" class="w-4 h-4" />
                  </button>
                </div>
              </div>
              <!-- Language row -->
              <div class="p-3 border-b border-gray-100 dark:border-gray-800">
                <p class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">{{ t('nav.language') }}</p>
                <div class="space-y-0.5">
                  <button
                    v-for="lang in languages"
                    :key="lang.code"
                    @click="switchLanguage(lang.code)"
                    :class="[
                      'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm transition-all duration-150',
                      currentLanguage.code === lang.code
                        ? 'bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                    ]"
                  >
                    <span class="text-base leading-none">{{ lang.flag }}</span>
                    <span class="font-medium flex-1 text-left">{{ lang.name }}</span>
                    <UIcon v-if="currentLanguage.code === lang.code" name="i-heroicons-check" class="w-4 h-4 text-violet-500" />
                  </button>
                </div>
              </div>
              <!-- Settings link -->
              <div class="p-2">
                <NuxtLink
                  to="/settings"
                  @click="closeAllMenus"
                  class="flex items-center gap-2.5 w-full px-2.5 py-2 rounded-lg text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-150"
                >
                  <UIcon name="i-heroicons-cog-6-tooth" class="w-4 h-4 text-gray-400" />
                  <span>{{ t('nav.settings') }}</span>
                  <UIcon name="i-heroicons-arrow-top-right-on-square" class="w-3.5 h-3.5 text-gray-400 ml-auto" />
                </NuxtLink>
              </div>
            </div>
          </Transition>
        </div>

        <!-- ④ User Dropdown -->
        <div class="relative user-profile-dropdown">
          <button
            @click="toggleUserMenu"
            class="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-150"
            :class="showUserMenu ? 'bg-gray-100 dark:bg-gray-800' : ''"
          >
            <div class="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-sm">
              <UIcon name="i-heroicons-user" class="w-4 h-4 text-white" />
            </div>
            <span class="hidden lg:block text-sm font-medium text-gray-700 dark:text-gray-300 max-w-[100px] truncate">
              {{ authStore.user?.username || 'User' }}
            </span>
            <UIcon name="i-heroicons-chevron-down" class="w-3.5 h-3.5 text-gray-400 transition-transform duration-200" :class="showUserMenu ? 'rotate-180' : ''" />
          </button>

          <Transition name="dropdown">
            <div v-if="showUserMenu" :class="isRTL ? 'left-0' : 'right-0'" class="dropdown-panel w-60">
              <!-- User info -->
              <div class="p-3 border-b border-gray-100 dark:border-gray-800">
                <div class="flex items-center gap-3">
                  <div class="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow">
                    <UIcon name="i-heroicons-user" class="w-5 h-5 text-white" />
                  </div>
                  <div class="min-w-0">
                    <p class="text-sm font-semibold text-gray-900 dark:text-white truncate">{{ authStore.user?.username || 'User' }}</p>
                    <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ authStore.user?.email }}</p>
                  </div>
                </div>
              </div>
              <!-- User links -->
              <div class="p-2 space-y-0.5">
                <p class="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 px-2 py-1">{{ t('nav.account') }}</p>
                <NuxtLink to="/profile" @click="closeAllMenus" class="dropdown-item">
                  <UIcon name="i-heroicons-user-circle" class="w-4 h-4 text-gray-400" />
                  <span class="text-sm">{{ t("nav.profile") }}</span>
                </NuxtLink>
                <NuxtLink to="/settings" @click="closeAllMenus" class="dropdown-item">
                  <UIcon name="i-heroicons-cog-6-tooth" class="w-4 h-4 text-gray-400" />
                  <span class="text-sm">{{ t("nav.settings") }}</span>
                </NuxtLink>
                <NuxtLink to="/reset-password" @click="closeAllMenus" class="dropdown-item">
                  <UIcon name="i-heroicons-key" class="w-4 h-4 text-gray-400" />
                  <span class="text-sm">{{ t("nav.resetPassword") }}</span>
                </NuxtLink>
                <NuxtLink to="/github" @click="closeAllMenus" class="dropdown-item">
                  <UIcon name="i-simple-icons-github" class="w-4 h-4 text-gray-400" />
                  <span class="text-sm">{{ t('nav.githubIntegration') }}</span>
                </NuxtLink>
              </div>
              <div class="p-2 border-t border-gray-100 dark:border-gray-800">
                <button @click="handleLogout" class="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors">
                  <UIcon name="i-heroicons-arrow-right-on-rectangle" class="w-4 h-4" />
                  <span>{{ t("nav.logout") }}</span>
                </button>
              </div>
            </div>
          </Transition>
        </div>
      </div>

      <!-- ── Mobile: hamburger + notif ── -->
      <div class="flex md:hidden items-center gap-2">
        <div class="relative notification-dropdown">
          <button @click="toggleNotifications" class="header-btn relative">
            <UIcon name="i-heroicons-bell" class="w-5 h-5" />
            <span v-if="unreadCount > 0" class="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {{ unreadCount > 9 ? '9+' : unreadCount }}
            </span>
          </button>
        </div>
        <button @click="toggleMobileMenu" class="header-btn">
          <UIcon :name="showMobileMenu ? 'i-heroicons-x-mark' : 'i-heroicons-bars-3'" class="w-5 h-5" />
        </button>
      </div>
    </div>

    <!-- ── Mobile Menu ── -->
    <Transition name="mobile-menu">
      <div v-if="showMobileMenu" class="md:hidden border-t border-gray-200 dark:border-gray-800 bg-white/95 dark:bg-gray-950/95 backdrop-blur-xl">
        <nav class="px-3 py-3 space-y-0.5">
          <p class="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 px-3 py-2">{{ t('nav.mainNavigation') }}</p>
          <NuxtLink
            v-for="item in mobileNavItems"
            :key="item.to"
            :to="item.to"
            v-show="item.show !== false"
            @click="closeMobileMenu"
            class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <UIcon :name="item.icon" class="w-5 h-5 text-gray-400" />
            <span class="font-medium text-sm">{{ item.label }}</span>
          </NuxtLink>

          <div class="border-t border-gray-200 dark:border-gray-800 my-2 pt-2">
            <p class="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 px-3 py-2">{{ t('nav.account') }}</p>
            <NuxtLink to="/profile" @click="closeMobileMenu" class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <UIcon name="i-heroicons-user-circle" class="w-5 h-5 text-gray-400" />
              <span class="font-medium text-sm">{{ t("nav.profile") }}</span>
            </NuxtLink>
            <NuxtLink to="/settings" @click="closeMobileMenu" class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <UIcon name="i-heroicons-cog-6-tooth" class="w-5 h-5 text-gray-400" />
              <span class="font-medium text-sm">{{ t("nav.settings") }}</span>
            </NuxtLink>
            <NuxtLink to="/github" @click="closeMobileMenu" class="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <UIcon name="i-simple-icons-github" class="w-5 h-5 text-gray-400" />
              <span class="font-medium text-sm">{{ t('nav.githubIntegration') }}</span>
            </NuxtLink>
            <button @click="handleLogout" class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
              <UIcon name="i-heroicons-arrow-right-on-rectangle" class="w-5 h-5" />
              <span class="font-medium text-sm">{{ t("nav.logout") }}</span>
            </button>
          </div>
        </nav>
      </div>
    </Transition>
  </header>
</template>

<script setup>
import { usePermissionStore } from "~/stores/permissions";
import { useNotifications } from "~/composables/useNotifications";

const { t } = useI18n();
const { isRTL, languages, currentLanguage, switchLanguage } = useLanguage();
const { themes, currentTheme, setTheme } = useTheme();
const router = useRouter();

const { notifications, unreadCount, connectionStatus, markAsRead, formatDate } = useNotifications();
const authStore = useAuthStore();
usePermissionStore();
const { hasPermission } = usePermissions();

// ── Dropdown state ──
const showNotifications = ref(false);
const showUserMenu = ref(false);
const showMobileMenu = ref(false);
const showMainMenu = ref(false);
const showSystemMenu = ref(false);

const closeAllMenus = () => {
  showNotifications.value = false;
  showUserMenu.value = false;
  showMainMenu.value = false;
  showSystemMenu.value = false;
  showMobileMenu.value = false;
};

const toggleNotifications = () => { showNotifications.value = !showNotifications.value; showUserMenu.value = false; showMainMenu.value = false; showSystemMenu.value = false; };
const toggleUserMenu      = () => { showUserMenu.value = !showUserMenu.value; showNotifications.value = false; showMainMenu.value = false; showSystemMenu.value = false; };
const toggleMainMenu      = () => { showMainMenu.value = !showMainMenu.value; showNotifications.value = false; showUserMenu.value = false; showSystemMenu.value = false; };
const toggleSystemMenu    = () => { showSystemMenu.value = !showSystemMenu.value; showNotifications.value = false; showUserMenu.value = false; showMainMenu.value = false; };
const toggleMobileMenu    = () => { showMobileMenu.value = !showMobileMenu.value; showNotifications.value = false; showUserMenu.value = false; };
const closeMobileMenu     = () => { showMobileMenu.value = false; };

// ── Nav items ──
const mainNavItems = computed(() => [
  {
    to: '/apps',
    label: t('nav.applications'),
    desc: t('nav.navDescriptions.apps'),
    icon: 'i-heroicons-squares-2x2',
    iconBg: 'bg-violet-100 dark:bg-violet-500/20',
    iconColor: 'text-violet-600 dark:text-violet-400',
    show: true,
  },
  {
    to: '/build-history',
    label: t('nav.buildHistory'),
    desc: t('nav.navDescriptions.buildHistory'),
    icon: 'i-heroicons-clock',
    iconBg: 'bg-blue-100 dark:bg-blue-500/20',
    iconColor: 'text-blue-600 dark:text-blue-400',
    show: true,
  },
  {
    to: '/build-analysis',
    label: t('nav.buildAnalysis'),
    desc: t('nav.navDescriptions.buildAnalysis'),
    icon: 'i-heroicons-chart-bar-square',
    iconBg: 'bg-emerald-100 dark:bg-emerald-500/20',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    show: true,
  },
  {
    to: '/log-analysis',
    label: t('nav.logAnalysis'),
    desc: t('nav.navDescriptions.logAnalysis'),
    icon: 'i-heroicons-document-magnifying-glass',
    iconBg: 'bg-amber-100 dark:bg-amber-500/20',
    iconColor: 'text-amber-600 dark:text-amber-400',
    show: hasPermission('logs', 'read') || authStore.user?.is_superuser,
  },
  {
    to: '/users',
    label: t('nav.users'),
    desc: t('nav.navDescriptions.users'),
    icon: 'i-heroicons-users',
    iconBg: 'bg-pink-100 dark:bg-pink-500/20',
    iconColor: 'text-pink-600 dark:text-pink-400',
    show: hasPermission('user', 'read'),
  },
]);

const mobileNavItems = computed(() => [
  { to: '/',               label: t('nav.home'),         icon: 'i-heroicons-home',                   show: true },
  { to: '/apps',           label: t('nav.applications'), icon: 'i-heroicons-squares-2x2',            show: true },
  { to: '/build-history',  label: t('nav.buildHistory'), icon: 'i-heroicons-clock',                  show: true },
  { to: '/build-analysis', label: t('nav.buildAnalysis'),icon: 'i-heroicons-chart-bar-square',       show: true },
  { to: '/log-analysis',   label: t('nav.logAnalysis'),  icon: 'i-heroicons-document-magnifying-glass', show: hasPermission('logs', 'read') || authStore.user?.is_superuser },
  { to: '/users',          label: t('nav.users'),        icon: 'i-heroicons-users',                  show: hasPermission('user', 'read') },
]);

// ── Notifications ──
const unreadNotifications = computed(() => notifications.value.filter((n) => n.status === 'unread'));

const handleNotificationClick = async (notification) => {
  try { await markAsRead(notification); } catch {}
  router.push('/notifications');
};

// ── Auth ──
const handleLogout = async () => {
  try { await authStore.logout(); router.push('/login'); } catch {}
};

// ── Outside click to close ──
onMounted(() => {
  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!t.closest('.notification-dropdown')) showNotifications.value = false;
    if (!t.closest('.user-profile-dropdown')) showUserMenu.value = false;
    if (!t.closest('.main-nav-dropdown'))     showMainMenu.value = false;
    if (!t.closest('.system-dropdown'))       showSystemMenu.value = false;
  });
});

watch(() => router.currentRoute.value.path, closeAllMenus);
</script>

<style scoped>
.header-btn {
  @apply flex items-center p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-150 text-sm font-medium;
}
.header-btn-active {
  @apply bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white;
}
.dropdown-panel {
  @apply absolute top-full mt-2 bg-white dark:bg-gray-900 rounded-xl shadow-xl border border-gray-200/80 dark:border-gray-700/80 z-50 overflow-hidden;
}
.dropdown-item {
  @apply flex items-center gap-3 w-full px-2.5 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-150 cursor-pointer;
}
.dropdown-item-active {
  @apply bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400;
}

/* Dropdown transition */
.dropdown-enter-active, .dropdown-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.dropdown-enter-from, .dropdown-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
}

/* Mobile menu transition */
.mobile-menu-enter-active, .mobile-menu-leave-active {
  transition: opacity 0.2s ease, max-height 0.2s ease;
  overflow: hidden;
  max-height: 500px;
}
.mobile-menu-enter-from, .mobile-menu-leave-to {
  opacity: 0;
  max-height: 0;
}
</style>
